import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { loadCatalog, parseFiltersFromSearch, computeActiveIndices } from '../lib/data'
import type { KV } from '@/lib/types'

function normalizeConfig(config?: KV[]) {
  const map: Record<string, any> = {}
  if (config && Array.isArray(config)) {
    for (const kv of config) {
      // Normalize key format: ensure consistent Title Case
      let normalizedKey: string
      if (kv.key.includes('_')) {
        // Convert snake_case to Title Case
        normalizedKey = kv.key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      } else if (kv.key.includes(' ')) {
        // Ensure each word is capitalized for space-separated keys
        normalizedKey = kv.key.replace(/\b\w/g, l => l.toUpperCase())
      } else {
        // Single word - capitalize first letter
        normalizedKey = kv.key.charAt(0).toUpperCase() + kv.key.slice(1).toLowerCase()
      }
      map[normalizedKey] = kv.value
    }
  }
  return map
}

function hashConfig(map: Record<string, any>): string {
  const keys = Object.keys(map).sort()
  const obj: Record<string, any> = {}
  for (const k of keys) obj[k] = map[k]
  const s = JSON.stringify(obj)
  // Simple hash
  let h = 2166136261 >>> 0
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619)
  return (h >>> 0).toString(16)
}

export default function ExplorePage() {
  const nav = useNavigate()
  const { search } = useLocation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<Awaited<ReturnType<typeof loadCatalog>> | null>(null)
  const filters = useMemo(() => parseFiltersFromSearch(search), [search])

  useEffect(() => {
    loadCatalog().then(setData).catch(e => setError(String(e))).finally(() => setLoading(false))
  }, [])

  const active = useMemo(() => (data ? computeActiveIndices(data, filters) : []), [data, filters])

  const boxes = useMemo(() => {
    if (!data) return [] as {
      cfgHash: string
      order: string[]
      map: Record<string, any>
      count: number
      runsCount: number
      artifactsCount: number
      disagree: Record<string, number>
    }[]

    const entries = active.map(i => data.all[i])
    const reps: Record<string, { order: string[]; map: Record<string, any>; count: number; runsCount: number; artifactsCount: number; disagree: Record<string, number> }> = {}

    // Get actively filtered features - when Result Analyzer is not filtered, entries with same config should be grouped
    const activeFilterKeys = Object.keys(filters).filter(k => {
      const filterSet = filters[k as keyof typeof filters]
      return filterSet && filterSet.size > 0
    })
    
    // Dedup configs (order-agnostic equality, keep first order for display)
    for (const e of entries) {
      const configMap = normalizeConfig(e.config)
      
      // Create a grouping key that combines config with actively filtered features only
      // This way, entries with same config but different unfiltered features get grouped together
      const groupingMap = { ...configMap }
      
      // Add actively filtered features to distinguish configs that differ on filtered dimensions
      // For multi-value features (arrays), only track which filter values match, not the full array
      for (const filterKey of activeFilterKeys) {
        if (e.features) {
          const featureValue = e.features.find((f: any) => f.key === filterKey)?.value
          if (featureValue !== undefined && featureValue !== null) {
            // If it's an array and we're filtering it, only include the matching filter values
            if (Array.isArray(featureValue) && filters[filterKey as keyof typeof filters]) {
              const filterSet = filters[filterKey as keyof typeof filters]
              if (filterSet) {
                const matchingValues = featureValue.filter(v => filterSet.has(v)).sort()
                if (matchingValues.length > 0) {
                  groupingMap[`_filter_${filterKey}`] = matchingValues.join(',')
                }
              }
            } else {
              groupingMap[`_filter_${filterKey}`] = featureValue
            }
          }
        }
      }
      
      const h = hashConfig(groupingMap)
      if (!reps[h]) {
        const configKeys = e.config && Array.isArray(e.config) ? e.config.map(kv => kv.key) : []
        reps[h] = { order: configKeys, map: configMap, count: 0, runsCount: 0, artifactsCount: 0, disagree: {} }
      }
      reps[h].count++
      
      // Check if this entry is a run or artifact
      const isRun = data.runs.some(r => r.path === e.path)
      if (isRun) {
        reps[h].runsCount++
      } else {
        reps[h].artifactsCount++
      }
    }

    const list = Object.entries(reps).map(([cfgHash, v]) => ({ cfgHash, ...v }))

    // Disagreement scores
    for (const a of list) {
      for (const k of a.order) {
        let d = 0
        for (const b of list) {
          if (b.cfgHash === a.cfgHash) continue
          if (!(k in b.map) || JSON.stringify(b.map[k]) !== JSON.stringify(a.map[k])) d++
        }
        a.disagree[k] = d
      }
    }

    // Sort keys inside each box by disagreement desc, stable by original order
    for (const a of list) {
      a.order = [...a.order].sort((x, y) => (a.disagree[y] - a.disagree[x]) || a.order.indexOf(x) - a.order.indexOf(y))
    }

    // Order boxes by item count (desc)
    return list.sort((a, b) => b.count - a.count)
  }, [data, active, filters])

  if (loading) return <div className="app">Loading…</div>
  if (error) return <div className="app" style={{ color: 'crimson' }}>Error: {error}</div>
  if (!data) return null

  return (
    <div className="app">
      <div className="header"><div className="title">Config Overview</div></div>
      <div className="subtext">Each distinct config corresponds to a unique set of hyperparameters/setup for the selected items. Empty configs represent metadata or aggregated results.</div>
      <div className="grid">
        {boxes.map(box => (
          <div key={box.cfgHash} className="card">
            <div className="card-head">
              <strong>Config</strong>
              <span className="count-pill">{box.runsCount} runs, {box.artifactsCount} artifacts</span>
            </div>
            <div className="mono">
              {box.order.map(k => (
                <div key={k}>
                  <span className="code-inline" style={{ fontWeight: 700 }}>{k}</span>
                  <span style={{ color: 'var(--accent)', fontStyle: 'italic' }}> {String(box.map[k])}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10 }}><button className="btn btn-primary" onClick={() => nav({ pathname: '/config', search: `${search}${search ? '&' : '?'}cfg=${box.cfgHash}` })}>Open</button></div>
          </div>
        ))}
      </div>
    </div>
  )
}


